<template>
  <div v-if="item.type === PREVIEW_ITEM.CUSTOMER_BUBBLE" class="col-span-3">
    <customer-bubble :class="{'cursor-pointer': item.isFirst}"
                     :customer-input="customerInput"
                     :focus="isCustomerBubbleFocused(item.isFirst)"
                     :is-clickable="item.isFirst"
                     data-cy="customer-bubble"
                     @click.native="selectCustomerBubble(item.text, item.isFirst)"
    />
  </div>

  <div v-else-if="item.type === PREVIEW_ITEM.BOT_BUBBLE" class="col-span-5">
    <email-bubble v-if="isEmailBot"
                  :state-id="item.node.stateId"
                  :action-id="item.node.actionId"
                  :is-template="item.node.isTemplate"
                  data-cy="email-bubble"
    />
    <!-- TODO rename chat bubble to bot bubble -->
    <chat-bubble v-else
                 :state-id="item.node.stateId"
                 :action-id="item.node.actionId"
                 data-cy="bot-bubble"
                 :is-template="item.node.isTemplate"
                 :in-nested-condition="inNestedCondition"
    />
  </div>

  <div v-else-if="item.type === PREVIEW_ITEM.WAIT_FOR_INPUT" class="col-span-5">
    <wait-for-input-bubble :state-id="item.stateId"
                           :next-state-id="item.nextStateId"
                           :validation-action-id="item.validationActionId"
                           :focus="isWaitForInputFocused(item.stateId, item.nextStateId)"
                           :is-template="item.isTemplate"
                           :context-state-id="item.contextStateId"
                           :in-nested-condition="inNestedCondition"
                           data-cy="wait-for-input-bubble"
    />
  </div>

  <div v-else-if="item.type === PREVIEW_ITEM.REMOTE_BUBBLE" class="col-span-5">
    <remote-bubble :state-id="item.node.stateId"
                   :action-id="item.node.actionId"
                   :is-template="item.node.isTemplate"
                   :in-nested-condition="inNestedCondition"
                   data-cy="remote-bubble"
                   :focus="isRemoteBubbleFocused(item.node.stateId, item.node.actionId)"
    />
  </div>

  <div v-else-if="item.type === PREVIEW_ITEM.CHAT_GPT_BUBBLE" class="col-span-5">
    <chat-GPT-bubble :state-id="item.node.stateId"
                     :action-id="item.node.actionId"
                     :is-template="item.node.isTemplate"
                     :in-nested-condition="inNestedCondition"
                     data-cy="chat-gpt-bubble"
                     :focus="isChatGPTBubbleFocused(item.node.stateId, item.node.actionId)"
    />
  </div>

  <div v-else-if="item.type === PREVIEW_ITEM.ADD_WIDGET" :class="getAddWidgetClasses()">
    <add-bubble-widget :node="item.node"
                       :answer-end-type="answerEndType"
                       :is-condition="item.isCondition"
                       :allow-condition="!item.params.disableCondition"
                       :disabled="!!item.params.disabled"
                       :disabled-hint="item.params.disabledHint"
                       :class="{'col-span-3 col-start-3': inCondition}"
                       data-cy="add-bubble-widget"
    />
  </div>

  <div v-else-if="item.type === PREVIEW_ITEM.ANSWER_END" :class="getAnswerEndClasses()">
    <answer-end :state-id="item.stateId"
                :type="item.value"
                :focus="isAnswerEndFocused(item.stateId, item.value, item.isCondition)"
                :is-template="item.isTemplate"
                :is-condition="item.isCondition"
                :is-postback="item.isPostback"
                :is-quick-reply="item.isQuickReply"
                :in-nested-condition="inNestedCondition"
                :condition-end-state-id="item.conditionEndStateId"
                :answer-id="item.answerId"
                data-cy="answer-end"
    />
  </div>

  <div v-else-if="item.type === PREVIEW_ITEM.CONDITION_SECTION" class="col-span-5">
    <condition-section :state-id="item.node.stateId"
                       :conversation-id="conversationId"
                       :conversation-type="conversationType"
                       :cases="item.node.cases"
                       :selected-case-items="item.caseItems"
                       :selected-case-index="item.caseSelected"
                       :is-highlighted="item.isHighlighted"
                       :is-template="item.node.isTemplate"
                       :in-nested-condition="inCondition"
    />
  </div>
</template>

<script>
import AnswerEnd from './AnswerEnd'
import ChatBubble from '../ChatBubble.vue'
import CustomerBubble from './CustomerBubble.vue'
import RemoteBubble from './RemoteBubble.vue'
import WaitForInputBubble from './WaitForInputBubble.vue'
import AddBubbleWidget from './AddBubbleWidget2.vue'
import ConditionSection from './ConditionSection2'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import { PREVIEW_ITEM } from '@/store/constants'
import EmailBubble from '@/components/preview/v2/EmailBubble'
import botModule from '@/store/modules/bot.module'
import ChatGPTBubble from '@/components/preview/v2/ChatGPTBubble.vue'
import variableRefsUtils from '@/utils/variable-refs.utils'

export default {
  components: {
    CustomerBubble,
    ChatBubble,
    AddBubbleWidget,
    WaitForInputBubble,
    AnswerEnd,
    RemoteBubble,
    ChatGPTBubble,
    ConditionSection,
    EmailBubble
  },

  name: 'PreviewItem',

  props: {
    conversationId: { type: String, required: true },
    conversationType: { type: String, required: true },
    answerEndType: { type: String, default: null },
    item: { required: true },
    inCondition: { type: Boolean, default: false },
    inNestedCondition: { type: Boolean, default: false }
  },

  data() {
    return {
      PREVIEW_ITEM: PREVIEW_ITEM
    }
  },

  computed: {
    bubbleSelected: chatBubbleModule.bubbleSelected,
    isEmailBot: botModule.isEmailBot,

    customerInput() {
      return variableRefsUtils.substituteVariableRefs(this.item.text)
    }
  },

  methods: {
    getAddWidgetClasses() {
      if (this.isEmailBot) return 'col-span-5'

      return this.inCondition ? 'grid grid-cols-5' : 'col-span-3 col-start-3'
    },

    getAnswerEndClasses() {
      if (this.isEmailBot) return 'col-span-5'

      return this.inCondition ? 'grid grid-cols-5' : 'col-span-3 col-start-3'
    },

    isAnswerEndFocused(stateId, type, isCondition) {
      return chatBubbleModule.isAnswerEndFocused(stateId, type, isCondition, this.inNestedCondition)
    },

    isCustomerBubbleFocused(isFirst) {
      return chatBubbleModule.isCustomerBubbleFocused(isFirst)
    },

    isWaitForInputFocused(stateId, nextStateId) {
      return chatBubbleModule.isWaitForInputFocused(stateId, nextStateId)
    },

    isRemoteBubbleFocused(stateId, actionId) {
      return chatBubbleModule.isRemoteBubbleFocused(stateId, actionId)
    },

    isChatGPTBubbleFocused(stateId, actionId) {
      return chatBubbleModule.isChatGPTBubbleFocused(stateId, actionId)
    },

    selectCustomerBubble(text, isFirst) {
      if (!isFirst) return

      chatBubbleModule.selectCustomerBubble(text, isFirst)
    }
  }
}
</script>
